import _ from 'lodash';

// The legacy Gauge chart definition a Gauge block saves as and draws with
// (components/Gauge). Named after its metric unless it's given another name.
const buildGaugeDefinition = ({
  card,
  id,
  name,
  metricDisplayName,
}: {
  card: Pick<Studio.Card, 'metricIds' | 'description' | 'gauge'>;
  id: string;
  name: string;
  // The gauge's label for its metric
  metricDisplayName: string;
}):
  | { chartDefinition: VisualisationDefinitions.Gauge }
  | { missing: string } => {
  const [metricId] = card.metricIds ?? [];
  if (!metricId) {
    return { missing: 'Add a metric' };
  }
  const { peerGroup, entity, isReversed } = card.gauge ?? {};
  if (!peerGroup || !entity) {
    return { missing: 'Choose a peer group and an entity to show a Gauge' };
  }
  return {
    chartDefinition: {
      id,
      name,
      description: card.description ?? '',
      type: 'Gauge',
      metricId,
      metricDisplayName,
      peerGroup,
      entity,
      ...(isReversed ? { isReversed: true } : {}),
    },
  };
};

// A Gauge block's settings from a saved Gauge
export const guessGaugeCard = (
  chartDef: VisualisationDefinitions.Gauge,
): Studio.GadgetSidecarCard => ({
  visType: 'gauge',
  metricIds: [chartDef.metricId],
  name: chartDef.name,
  ...(chartDef.description ? { description: chartDef.description } : {}),
  gauge: _.omitBy(
    {
      peerGroup: chartDef.peerGroup,
      entity: chartDef.entity,
      isReversed: chartDef.isReversed || undefined,
    },
    _.isUndefined,
  ),
});

// Without what the classic editor saves as empty, what's kept for soft
// deletes, and the version it saved, which nothing reads
const toComparable = (def: VisualisationDefinitions.Gauge) =>
  _.omitBy(
    {
      ..._.omit(def, ['isHidden', 'hiddenOn', 'version']),
      description: def.description || undefined,
      isReversed: def.isReversed || undefined,
    },
    _.isUndefined,
  ) as Record<string, unknown>;

// What the settings don't rebuild of the saved Gauge, by setting (empty when
// they rebuild it all). Its name and metric label are the gadget's own (the
// classic editor can change them).
export const getGaugeChanges = ({
  card,
  chartDef,
}: {
  card: Studio.GadgetSidecarCard;
  chartDef: VisualisationDefinitions.Gauge;
}): string[] => {
  if (card.visType !== 'gauge') {
    return ['Chart type'];
  }
  const built = buildGaugeDefinition({
    card,
    id: chartDef.id,
    name: chartDef.name,
    metricDisplayName: chartDef.metricDisplayName,
  });
  if (!('chartDefinition' in built)) {
    return [built.missing];
  }
  const before = toComparable(chartDef);
  const after = toComparable(built.chartDefinition);
  return _.uniq([...Object.keys(before), ...Object.keys(after)])
    .filter((key) => !_.isEqual(before[key], after[key]))
    .map((key) => _.startCase(key));
};

// Whether the settings rebuild the saved Gauge
export const reproducesGauge = (
  args: Parameters<typeof getGaugeChanges>[0],
): boolean => getGaugeChanges(args).length === 0;

export default buildGaugeDefinition;
